import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatSuggestedEvent, UnifiedChatRichContextEntry } from '../../../data-access';
import type { NearbyPlace } from '../../../data-access/api/science/openapi/api';
import type { ProjectFilterItem } from '../../../hooks';
import SuggestedBlockRow from './SuggestedBlockRow';
import ApproveDismissBar from './ApproveDismissBar';
import { SuggestedBlockHandle, SuggestedBlockActionsVisibility } from './types';
import { sortSuggestedBlocksByDate } from '../AssistantChatSuggestedBlocks/helpers';
import './SuggestionGroupCard.scss';

// Stable fallback reference — `?? []` would allocate a new array every render,
// which breaks memo() on every block that has no nearby places.
const EMPTY_NEARBY_PLACES: NearbyPlace[] = [];
const EMPTY_RICH_CONTEXT_ENTRIES: UnifiedChatRichContextEntry[] = [];

interface SuggestionGroupCardProps {
  blocks: ChatSuggestedEvent[];
  loadingEventId?: string;
  processedIds: string[];
  onProcessedIdsChange: (ids: string[]) => void;
  onManageCalendar?: (e: ChatSuggestedEvent) => Promise<boolean>;
  onCheckCalendar?: (startTime: string) => void;
  onManageTodo?: (e: ChatSuggestedEvent, board: ProjectFilterItem) => Promise<boolean>;
  nearbyPlacesByEventId?: Record<string, NearbyPlace[]>;
  /** 'adjustment'-sourced richContext entries matched to the block they
   * belong to — rendered inside that EventBlock's own card. */
  richContextEntriesByEventId?: Record<string, UnifiedChatRichContextEntry[]>;
  /** Clears the block's optimistic calendar placeholder, if any, on discard. */
  onDiscardBlock?: (eventId: string) => void;
  /** Clears optimistic calendar placeholders for all pending blocks on discard all. */
  onDiscardAllBlocks?: (eventIds: string[]) => void;
  /** Block ids that were accepted by the user's auto-accept settings rather
   * than a manual click — their action row shows a disabled "Auto Accepted"
   * pill instead of hiding once accepted. */
  autoAcceptedIds?: Set<string>;
}

/**
 * Grouped suggestion list — header with count, item cards, and a bottom
 * Approve All/Dismiss All bar. Discarded or accepted items stay visible;
 * only their Accept/Discard row disappears (a loader while Approve All / an
 * individual accept is in flight).
 */
const SuggestionGroupCard: React.FC<SuggestionGroupCardProps> = ({
  blocks,
  loadingEventId,
  processedIds,
  onProcessedIdsChange,
  onManageCalendar,
  onCheckCalendar,
  onManageTodo,
  nearbyPlacesByEventId = {},
  richContextEntriesByEventId = {},
  onDiscardBlock,
  onDiscardAllBlocks,
  autoAcceptedIds,
}) => {
  const { t } = useTranslation();

  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [isApprovingAll, setIsApprovingAll] = useState(false);
  const rowRefs = useRef<Record<string, SuggestedBlockHandle | null>>({});

  const sortedBlocks = useMemo(() => sortSuggestedBlocksByDate(blocks), [blocks]);

  const pendingBlocks = useMemo(
    () =>
      sortedBlocks.filter(
        (b) => !dismissedIds.includes(b.eventId) && !processedIds.includes(b.eventId)
      ),
    [sortedBlocks, dismissedIds, processedIds]
  );

  const handleDiscard = useCallback(
    (eventId: string) => {
      setDismissedIds((prev) => (prev.includes(eventId) ? prev : [...prev, eventId]));
      onDiscardBlock?.(eventId);
    },
    [onDiscardBlock]
  );

  const handleDismissAll = useCallback(() => {
    const ids = pendingBlocks.map((b) => b.eventId);
    setDismissedIds((prev) => [...prev, ...ids]);
    if (onDiscardAllBlocks) {
      onDiscardAllBlocks(ids);
    } else {
      ids.forEach((id) => onDiscardBlock?.(id));
    }
  }, [pendingBlocks, onDiscardAllBlocks, onDiscardBlock]);

  const handleApproveAll = useCallback(async () => {
    setIsApprovingAll(true);
    try {
      // Sequential, not Promise.all: some per-item accept flows show a
      // confirm dialog, which would stack unreadably if fired concurrently.
      for (const block of pendingBlocks) {
        await rowRefs.current[block.eventId]?.accept();
      }
    } finally {
      setIsApprovingAll(false);
    }
  }, [pendingBlocks]);

  if (!sortedBlocks.length) {
    return null;
  }

  return (
    <div className="suggestion-group-card">
      <div className="suggestion-group-card__header">
        <span className="suggestion-group-card__header-title">
          {t('task.ai.suggestedBlocks.groupTitle')}
        </span>
        <span className="suggestion-group-card__count-badge">{pendingBlocks.length}</span>
      </div>
      <div className="suggestion-group-card__list">
        {sortedBlocks.map((block) => {
          const key = block.linkedMessageId || block.eventId;
          const isProcessed = processedIds.includes(block.eventId);
          const isDone = dismissedIds.includes(block.eventId) || isProcessed;
          const isLoadingItem = isApprovingAll || loadingEventId === block.eventId;
          const isAutoAccepted = !!autoAcceptedIds?.has(block.eventId);
          const actionsVisibility: SuggestedBlockActionsVisibility = isAutoAccepted
            ? 'auto-accepted'
            : isDone
              ? 'hidden'
              : isLoadingItem
                ? 'loading'
                : 'visible';
          return (
            <SuggestedBlockRow
              key={key}
              ref={(handle) => {
                rowRefs.current[block.eventId] = handle;
              }}
              block={block}
              loadingEventId={isLoadingItem ? block.eventId : undefined}
              processedIds={processedIds}
              onProcessedIdsChange={onProcessedIdsChange}
              onManageCalendar={onManageCalendar}
              onCheckCalendar={onCheckCalendar}
              onManageTodo={onManageTodo}
              nearbyPlaces={nearbyPlacesByEventId[block.eventId] ?? EMPTY_NEARBY_PLACES}
              richContextEntries={
                richContextEntriesByEventId[block.eventId] ?? EMPTY_RICH_CONTEXT_ENTRIES
              }
              onDiscard={handleDiscard}
              actionsVisibility={actionsVisibility}
              defaultExpanded={false}
            />
          );
        })}
      </div>
      {!!pendingBlocks.length && (
        <ApproveDismissBar
          approveLabel={t('task.ai.suggestedBlocks.approveAll')}
          dismissLabel={t('task.ai.suggestedBlocks.dismissAll')}
          onApproveAll={handleApproveAll}
          onDismissAll={handleDismissAll}
          disabled={isApprovingAll}
          loading={isApprovingAll}
        />
      )}
    </div>
  );
};

export default SuggestionGroupCard;
